import { useScienceTutorSection } from './scienceTutorContext';
import {
  ArrowLeft,
  ArrowRight,
  Beaker,
  Calculator,
  CheckCircle2,
  FlaskConical,
  RotateCcw,
  Thermometer,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  FOUNDATION_MIXTURES,
  MIXTURE_EVIDENCE_LABELS,
  MIXTURE_OUTCOME_LABELS,
  SEPARATION_METHOD_LABELS,
  SOLUTIONS_TOOL_IDENTITY,
  TRANSFER_CHALLENGE,
  checkMixtureEvidence,
  checkMixturePrediction,
  checkTransferChallenge,
  type FoundationMixture,
  type MixtureEvidence,
  type MixtureOutcome,
  type SeparationMethod,
  type SolutionMode,
} from './solutionsLab';
import { StudyShell } from './StudyShell';
import { CrystalExplore } from './SolutionsCrystalExplore';
import { CrystalPractice } from './SolutionsCrystalPractice';
import { SeniorMode } from './SolutionsSeniorMode';
import { type SolutionsView, ViewSwitch } from './SolutionsViewSwitch';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  SolutionMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  'year-7': {
    tab: 'Year 7',
    eyebrow: 'Solutions and mixtures',
    title: 'Will it dissolve, settle or separate?',
    description:
      'Predict the visible result, then connect the observation to a simple particle model.',
  },
  'year-10': {
    tab: 'Year 10',
    eyebrow: 'Solubility and crystallisation',
    title: 'How much solute can the water hold?',
    description:
      'Type the water, solute and temperatures, see the worked limits, then practise predicting crystals.',
  },
  senior: {
    tab: 'Senior',
    eyebrow: 'Concentration and dilution',
    title: 'Prepare a solution from a stock',
    description:
      'Type C₁, V₁ and V₂ to see C₂ = C₁ × V₁ ÷ V₂ worked, then practise the calculation.',
  },
};

const OUTCOME_OPTIONS: MixtureOutcome[] = ['solution', 'suspension', 'layer'];

const OUTCOME_VISUAL_SUMMARIES: Record<MixtureOutcome, string> = {
  solution: 'Clear and uniform throughout',
  suspension: 'Visible grains settle at the bottom',
  layer: 'Two distinct liquid layers form',
};

const OUTCOME_SHORT_LABELS: Record<MixtureOutcome, string> = {
  solution: 'Clear solution',
  suspension: 'Settles',
  layer: 'Two layers',
};

const PARTICLE_POSITIONS = [
  { left: 18, top: 34 },
  { left: 38, top: 28 },
  { left: 61, top: 38 },
  { left: 79, top: 25 },
  { left: 26, top: 54 },
  { left: 50, top: 49 },
  { left: 72, top: 58 },
  { left: 15, top: 72 },
  { left: 39, top: 69 },
  { left: 63, top: 76 },
  { left: 84, top: 67 },
  { left: 28, top: 86 },
  { left: 52, top: 88 },
  { left: 76, top: 85 },
] as const;

function Vessel({
  mixture,
  revealed,
}: {
  mixture: FoundationMixture;
  revealed: boolean;
}) {
  const outcome = revealed ? mixture.outcome : null;
  return (
    <div
      className="relative mx-auto h-[25rem] w-full max-w-md overflow-hidden rounded-[2rem] border border-brand-sky/20 bg-gradient-to-br from-wash-sky via-white to-wash-mint shadow-card-soft sm:h-[22rem]"
      role="img"
      aria-label={
        revealed
          ? `${mixture.name}: ${MIXTURE_OUTCOME_LABELS[mixture.outcome]}`
          : `Water before adding ${mixture.addedMaterial.toLowerCase()}`
      }
    >
      <div className="absolute inset-x-5 top-5 z-20 flex items-center justify-between gap-3">
        <span className="rounded-full bg-white/90 px-3 py-1.5 text-[0.65rem] font-extrabold uppercase tracking-[0.14em] text-brand-sky shadow-sm">
          Macroscopic view
        </span>
        <span className="rounded-full border border-white bg-white/80 px-3 py-1.5 text-xs font-extrabold text-ink-soft shadow-sm">
          {revealed ? OUTCOME_SHORT_LABELS[mixture.outcome] : 'Ready to mix'}
        </span>
      </div>

      <div className="absolute left-1/2 top-[4.6rem] h-64 w-64 -translate-x-1/2">
        <div
          className={`absolute right-9 top-[-1.2rem] z-20 h-44 w-2 origin-top rotate-[14deg] rounded-full border border-hairline bg-white/90 shadow-sm transition-transform duration-500 motion-reduce:transition-none ${
            revealed ? 'rotate-[5deg]' : ''
          }`}
          aria-hidden="true"
        />

        <div className="absolute inset-x-2 top-0 z-20 h-7 rounded-[50%] border-[3px] border-brand-sky/35 bg-white/75 shadow-sm" />
        <div className="absolute inset-x-3 top-2 h-[14.2rem] overflow-hidden rounded-b-[3rem] border-x-[3px] border-b-[4px] border-brand-sky/30 bg-white/40 shadow-inner">
          <div className="absolute inset-x-2 bottom-2 h-[74%] overflow-hidden rounded-b-[2.5rem] bg-gradient-to-b from-brand-sky/20 via-wash-sky to-brand-sky/35">
            <div className="absolute inset-x-0 top-[-0.45rem] h-4 rounded-[50%] border border-brand-sky/25 bg-white/50" />
            {outcome === 'layer' ? (
              <div className="absolute inset-x-0 top-0 h-[34%] border-b-2 border-dashed border-brand-sunshine bg-brand-sunshine/70" />
            ) : null}

            {revealed
              ? PARTICLE_POSITIONS.map((position, index) => {
                  const particleStyle =
                    outcome === 'suspension'
                      ? {
                          left: `${position.left}%`,
                          top: `${Math.max(70, position.top + (index % 3) * 5)}%`,
                        }
                      : outcome === 'layer'
                        ? {
                            left: `${position.left}%`,
                            top: `${10 + (index % 4) * 5}%`,
                          }
                        : { left: `${position.left}%`, top: `${position.top}%` };
                  return (
                    <span
                      key={`${mixture.id}-${index}`}
                      style={particleStyle}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 shadow-sm transition-all duration-500 motion-reduce:transition-none ${
                        outcome === 'solution'
                          ? index % 2
                            ? 'h-3 w-3 border-brand-coral bg-brand-coral'
                            : 'h-3 w-3 border-brand-sky bg-white'
                          : outcome === 'suspension'
                            ? index % 3
                              ? 'h-3.5 w-3.5 border-ink-soft bg-brand-sunshine'
                              : 'h-5 w-5 border-ink-soft bg-brand-coral'
                            : index % 3
                              ? 'h-4 w-6 border-brand-sunshine bg-wash-sunshine'
                              : 'h-5 w-8 border-brand-sunshine bg-surface-soft'
                      }`}
                      aria-hidden="true"
                    />
                  );
                })
              : null}
          </div>

          <div className="absolute left-3 top-12 grid gap-7" aria-hidden="true">
            {[4, 3, 4, 3].map((width, index) => (
              <div key={index} className="flex items-center">
                <span className={`h-px bg-brand-sky/50 ${width === 4 ? 'w-4' : 'w-3'}`} />
              </div>
            ))}
          </div>
          <div className="absolute bottom-3 right-4 h-32 w-5 rounded-full bg-white/30 blur-sm" aria-hidden="true" />
        </div>
      </div>

      <div className="absolute inset-x-5 bottom-4 z-30 flex flex-col items-start gap-2 rounded-2xl border border-white bg-white/85 px-4 py-3 shadow-sm backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <p className="text-[0.6rem] font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            {revealed ? 'What you can see' : 'Starting condition'}
          </p>
          <p className="text-xs font-extrabold text-ink">
            {revealed
              ? OUTCOME_VISUAL_SUMMARIES[mixture.outcome]
              : `Clear water + ${mixture.addedMaterial.toLowerCase()}`}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-wash-sunshine px-3 py-1.5 text-[0.65rem] font-bold text-slate2">
          <span className="sm:hidden">Not to scale</span>
          <span className="hidden sm:inline">Particles not to scale</span>
        </span>
      </div>
    </div>
  );
}

function FoundationMode() {
  const [mixtureId, setMixtureId] = useState(FOUNDATION_MIXTURES[0].id);
  const [prediction, setPrediction] = useState<MixtureOutcome | ''>('');
  const [revealed, setRevealed] = useState(false);
  const [evidence, setEvidence] = useState<MixtureEvidence | ''>('');
  const [evidenceChecked, setEvidenceChecked] = useState(false);
  const [completedMixtures, setCompletedMixtures] = useState<FoundationMixture['id'][]>([]);
  const [transferOutcome, setTransferOutcome] = useState<MixtureOutcome | ''>('');
  const [transferMethod, setTransferMethod] = useState<SeparationMethod | ''>('');
  const [transferChecked, setTransferChecked] = useState(false);
  useScienceTutorSection('FoundationMode', { mixtureId, prediction, revealed, evidence, evidenceChecked, transferOutcome, transferMethod, transferChecked });
  const mixture =
    FOUNDATION_MIXTURES.find((candidate) => candidate.id === mixtureId) ??
    FOUNDATION_MIXTURES[0];
  const result = prediction ? checkMixturePrediction(mixture, prediction) : null;
  const evidenceResult = evidence ? checkMixtureEvidence(mixture, evidence) : null;
  // The transfer challenge opens once one mixture of every kind has been compared.
  const allCompared = OUTCOME_OPTIONS.every((option) =>
    FOUNDATION_MIXTURES.some(
      (candidate) => candidate.outcome === option && completedMixtures.includes(candidate.id),
    ),
  );
  const transferResult =
    transferOutcome && transferMethod
      ? checkTransferChallenge(TRANSFER_CHALLENGE, transferOutcome, transferMethod)
      : null;
  const nextMixture = FOUNDATION_MIXTURES.find(
    (candidate) => !completedMixtures.includes(candidate.id),
  );

  const reset = (nextMixture: FoundationMixture = mixture) => {
    setMixtureId(nextMixture.id);
    setPrediction('');
    setRevealed(false);
    setEvidence('');
    setEvidenceChecked(false);
  };

  const checkEvidence = () => {
    if (!evidenceResult) return;
    setEvidenceChecked(true);
    if (evidenceResult.correct) {
      setCompletedMixtures((current) =>
        current.includes(mixture.id) ? current : [...current, mixture.id],
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              Compare the mixtures
            </p>
            <span className="rounded-full bg-wash-mint px-3 py-1 text-xs font-extrabold text-ink-soft">
              {completedMixtures.length} of {FOUNDATION_MIXTURES.length}
            </span>
          </div>
          <div className="mt-4 grid gap-2">
            {FOUNDATION_MIXTURES.map((candidate) => {
              const completed = completedMixtures.includes(candidate.id);
              return (
                <button
                  key={candidate.id}
                  type="button"
                  aria-pressed={candidate.id === mixture.id}
                  onClick={() => reset(candidate)}
                  className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                    candidate.id === mixture.id
                      ? 'border-brand-sky bg-wash-sky text-ink'
                      : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
                  }`}
                >
                  <span className="flex items-center justify-between gap-3 text-sm font-extrabold">
                    {candidate.name}
                    {completed ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-mint" aria-label="Compared" />
                    ) : null}
                  </span>
                  <span className="mt-1 block text-xs font-medium">{candidate.addedMaterial}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-6 rounded-2xl bg-wash-sunshine p-4 text-sm font-medium leading-6 text-ink-soft">
            A solution is one uniform phase. A cloudy suspension or a second liquid layer is still a
            mixture, but it is not a solution.
          </div>
        </aside>

        <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)] lg:items-center">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
                Predict before mixing
              </p>
              <h2 className="mt-2 text-2xl font-extrabold text-ink">{mixture.prompt}</h2>
              <div className="mt-5 grid gap-2" aria-label="Choose the observed result">
                {OUTCOME_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={prediction === option}
                    disabled={revealed}
                    onClick={() => setPrediction(option)}
                    className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-not-allowed ${
                      prediction === option
                        ? 'border-brand-coral bg-wash-coral text-ink'
                        : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
                    }`}
                  >
                    {MIXTURE_OUTCOME_LABELS[option]}
                  </button>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={!prediction || revealed}
                  onClick={() => setRevealed(true)}
                  className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Mix and observe
                </button>
                <button
                  type="button"
                  onClick={() => reset()}
                  className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-5 py-3 text-sm font-extrabold text-ink-soft"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
                </button>
              </div>
            </div>
            <Vessel mixture={mixture} revealed={revealed} />
          </div>

          {revealed && result ? (
            <div className="mt-7 space-y-5">
              <div
                role="status"
                className={`rounded-[1.5rem] border p-5 ${
                  result.correct
                    ? 'border-brand-mint/45 bg-wash-mint'
                    : 'border-brand-coral/45 bg-wash-coral'
                }`}
              >
                <div className="flex items-center gap-2 font-extrabold text-ink">
                  {result.correct ? (
                    <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                  ) : (
                    <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
                  )}
                  {result.correct
                    ? `Correct — ${MIXTURE_OUTCOME_LABELS[mixture.outcome]}`
                    : `Compare your choice with ${MIXTURE_OUTCOME_LABELS[mixture.outcome]}`}
                </div>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{result.feedback}</p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                  <strong>Particle view:</strong> {mixture.particleExplanation}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                  <strong>Recover it:</strong> {mixture.recoveryMethod}
                </p>
              </div>

              <section className="rounded-[1.5rem] border border-brand-sky/25 bg-wash-sky p-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
                  Use the observation as evidence
                </p>
                <h3 className="mt-2 text-lg font-extrabold text-ink">
                  What is the strongest evidence for this classification?
                </h3>
                <div className="mt-4 grid gap-2 sm:grid-cols-3" aria-label="Choose the strongest evidence">
                  {(Object.keys(MIXTURE_EVIDENCE_LABELS) as MixtureEvidence[]).map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={evidence === option}
                      onClick={() => {
                        setEvidence(option);
                        setEvidenceChecked(false);
                      }}
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${
                        evidence === option
                          ? 'border-brand-sky bg-white text-ink'
                          : 'border-white bg-white/60 text-ink-soft'
                      }`}
                    >
                      {MIXTURE_EVIDENCE_LABELS[option]}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={!evidence}
                  onClick={checkEvidence}
                  className="mt-4 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Check the evidence
                </button>

                {evidenceChecked && evidenceResult ? (
                  <div
                    role="region"
                    aria-label="Evidence feedback"
                    className={`mt-4 rounded-2xl border p-4 ${
                      evidenceResult.correct
                        ? 'border-brand-mint/45 bg-wash-mint'
                        : 'border-brand-coral/45 bg-wash-coral'
                    }`}
                  >
                    <p className="font-extrabold text-ink">
                      {evidenceResult.correct ? 'That evidence supports the classification' : 'Look again at the beaker'}
                    </p>
                    <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">
                      {evidenceResult.feedback}
                    </p>
                    {evidenceResult.correct && nextMixture ? (
                      <button
                        type="button"
                        onClick={() => reset(nextMixture)}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-extrabold text-white"
                      >
                        Compare {nextMixture.name} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : null}
                    {evidenceResult.correct && !nextMixture ? (
                      <p className="mt-2 text-sm font-extrabold text-brand-mint">
                        All three mixtures compared. Try the new situation below.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </div>
          ) : null}
        </article>
      </div>

      {allCompared ? (
        <section className="rounded-[2rem] border border-brand-mint/35 bg-gradient-to-br from-white to-wash-mint p-5 shadow-card-soft sm:p-6 lg:p-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
                Apply the idea to a new mixture
              </p>
              <h2 className="mt-2 text-2xl font-extrabold text-ink">{TRANSFER_CHALLENGE.title}</h2>
              <p className="mt-3 font-medium leading-7 text-ink-soft">{TRANSFER_CHALLENGE.scenario}</p>
              <p className="mt-3 text-sm font-extrabold text-ink">{TRANSFER_CHALLENGE.question}</p>
            </div>
            <div className="grid gap-5">
              <div>
                <p className="text-sm font-extrabold text-ink">Classification</p>
                <div className="mt-2 grid gap-2">
                  {OUTCOME_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={transferOutcome === option}
                      onClick={() => {
                        setTransferOutcome(option);
                        setTransferChecked(false);
                      }}
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${
                        transferOutcome === option
                          ? 'border-brand-coral bg-wash-coral'
                          : 'border-hairline bg-white'
                      }`}
                    >
                      {MIXTURE_OUTCOME_LABELS[option]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-extrabold text-ink">Recover the chalk</p>
                <div className="mt-2 grid gap-2">
                  {(Object.keys(SEPARATION_METHOD_LABELS) as SeparationMethod[]).map((method) => (
                    <button
                      key={method}
                      type="button"
                      aria-pressed={transferMethod === method}
                      onClick={() => {
                        setTransferMethod(method);
                        setTransferChecked(false);
                      }}
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${
                        transferMethod === method
                          ? 'border-brand-sky bg-wash-sky'
                          : 'border-hairline bg-white'
                      }`}
                    >
                      {SEPARATION_METHOD_LABELS[method]}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                disabled={!transferOutcome || !transferMethod}
                onClick={() => setTransferChecked(true)}
                className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check the new situation
              </button>
            </div>
          </div>

          {transferChecked && transferResult ? (
            <div
              role="region"
              aria-label="Transfer challenge feedback"
              className={`mt-6 rounded-[1.5rem] border p-5 ${
                transferResult.correct
                  ? 'border-brand-mint/45 bg-wash-mint'
                  : 'border-brand-coral/45 bg-wash-coral'
              }`}
            >
              <p className="text-lg font-extrabold text-ink">
                {transferResult.correct ? 'Activity completed — you applied the idea' : 'Use the observation, then try again'}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                {transferResult.feedback}
              </p>
              {transferResult.correct ? (
                <>
                  <div className="mt-5 grid gap-3 sm:hidden">
                    {FOUNDATION_MIXTURES.map((candidate) => (
                      <article key={candidate.id} className="rounded-2xl bg-white/85 p-4">
                        <h3 className="font-extrabold text-ink">{candidate.name}</h3>
                        <dl className="mt-3 grid gap-2 text-sm text-ink-soft">
                          <div>
                            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Classification</dt>
                            <dd className="mt-0.5">{MIXTURE_OUTCOME_LABELS[candidate.outcome]}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Evidence</dt>
                            <dd className="mt-0.5">{MIXTURE_EVIDENCE_LABELS[candidate.evidence]}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Recovery</dt>
                            <dd className="mt-0.5">{candidate.recoveryMethod}</dd>
                          </div>
                        </dl>
                      </article>
                    ))}
                  </div>
                  <div className="mt-5 hidden overflow-x-auto sm:block">
                    <table className="w-full min-w-[36rem] border-separate border-spacing-y-2 text-left text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate2">
                      <tr>
                        <th className="px-3 py-1">Mixture</th>
                        <th className="px-3 py-1">Classification</th>
                        <th className="px-3 py-1">Evidence</th>
                        <th className="px-3 py-1">Recovery</th>
                      </tr>
                    </thead>
                    <tbody>
                      {FOUNDATION_MIXTURES.map((candidate) => (
                        <tr key={candidate.id} className="bg-white/85 text-ink-soft">
                          <td className="rounded-l-xl px-3 py-3 font-extrabold text-ink">{candidate.name}</td>
                          <td className="px-3 py-3">{MIXTURE_OUTCOME_LABELS[candidate.outcome]}</td>
                          <td className="px-3 py-3">{MIXTURE_EVIDENCE_LABELS[candidate.evidence]}</td>
                          <td className="rounded-r-xl px-3 py-3">{candidate.recoveryMethod}</td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

/** Year 10: Explore (own inputs, worked calculation, 3D beaker) or Practice (predict, then calculate). */
function CrystalMode() {
  const [view, setView] = useState<SolutionsView>('explore');
  useScienceTutorSection('CrystalMode', { view });
  return (
    <div>
      <ViewSwitch view={view} onChange={setView} />
      {view === 'explore' ? <CrystalExplore /> : <CrystalPractice />}
    </div>
  );
}

const SOLUTION_MODE_YEARS: readonly { mode: SolutionMode; fromYear: number }[] = [
  { mode: 'year-7', fromYear: 6 },
  { mode: 'year-10', fromYear: 10 },
  { mode: 'senior', fromYear: 11 },
];

export function SolutionsLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<SolutionMode>(() => modeForYear(SOLUTION_MODE_YEARS, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'solutions', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={SOLUTIONS_TOOL_IDENTITY.toolKey}
        data-tool-version={SOLUTIONS_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Beaker className="h-4 w-4" aria-hidden="true" /> Solutions and concentration
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Solutions, Solubility &amp; Concentration Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Follow one idea from visible mixtures to solubility curves and quantitative dilution.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 7–12 · progressive model
          </div>
        </div>

        <div
          className="mt-6 grid max-w-3xl grid-cols-3 rounded-full bg-white/75 p-1"
          aria-label="Choose learning level"
        >
          {(Object.keys(MODE_COPY) as SolutionMode[]).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              {copy.eyebrow}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            {mode === 'year-7' ? (
              <FlaskConical className="h-4 w-4" aria-hidden="true" />
            ) : mode === 'year-10' ? (
              <Thermometer className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Calculator className="h-4 w-4" aria-hidden="true" />
            )}
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'year-7' ? <FoundationMode /> : null}
      {mode === 'year-10' ? <CrystalMode /> : null}
      {mode === 'senior' ? <SeniorMode /> : null}

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases. It does not replace a supervised practical,
        determine laboratory safety, save an assessment attempt or claim alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
